/* eslint-env browser */
import debounce from "lodash/debounce";
import { Controller } from "stimulus";
import useStatusClasses from "../../composables/use_status_classes";
import useEventController from "../../composables/use_event_controller";

export default class extends Controller {
  static targets = ["button", "checkbox", "checkmark", "input", "invisibleLabel", "formValue"];

  static values = {
    dirty: { type: Boolean, default: false },
    status: { type: String, default: "need_to_receive" },
    allocation: { type: Number, default: 0 },
    initialAllocation: { type: Number, default: 0 },
    maxAllocation: { type: Number, default: 0 },
  };

  updateStatus = debounce(this.#updateStatus.bind(this), 10);

  connect() {
    useEventController(this);
    const row = this.element.closest("[data-component=groupRow]");
    this.listenOn(document, `receivingRowToggleRow:${row.dataset.domId}`, this.#togglingRowCheckbox.bind(this));
    this.listenOn(document, "receivingReset", this.#reset.bind(this));
    this.listenOn(document, "receivingRefresh", this.#refresh.bind(this));
    this.listenOn(this.element, "receivingAllocation", (e) =>
      this.#updateAllocation({ value: e.detail.value, emitEvents: e.detail.mainUpdated }),
    );

    useStatusClasses(this, "good");

    Object.assign(this, {
      allocationValueChanged: this.#allocationUpdated.bind(this),
    });

    this.updateStatusClasses(this.statusValue, undefined);

    this.emitEvents = true;
  }

  initialAllocationValueChanged() {
    this.updateStatus();
  }

  statusValueChanged(value, previousValue) {
    this.element.dataset.status = value;
    this.checkboxTarget.classList.toggle("hidden", value === "received");
    if (this.hasCheckmarkTarget) {
      this.checkmarkTarget.classList.toggle("hidden", value !== "received");
    }

    this.updateStatusClasses?.(value, previousValue);
  }

  checkboxChanged() {
    if (!this.checkboxTarget.checked) {
      this.allocationValue = this.initialAllocationValue;
    } else if (!this.#allocationMaxed) {
      this.allocationValue = this.maxAllocationValue;
    }
    this.updateStatus();
  }

  update() {
    this.#updateAllocation({ value: this.inputTarget.value, emitEvents: true });
  }

  #refresh() {
    this.initialAllocationValue = this.allocationValue;
    this.#uncheck();
  }

  #reset() {
    this.allocationValue = this.initialAllocationValue;
    this.#uncheck();
  }

  #uncheck() {
    this.checkboxTarget.indeterminate = false;
    this.checkboxTarget.checked = false;
    this.updateStatus();
  }

  #updateAllocation({ value, emitEvents }) {
    this.emitEvents = emitEvents;
    this.allocationValue = parseInt(value, 10);

    setTimeout(() => {
      this.emitEvents = true;
      this.updateStatus();
    });
  }

  #updateStatus() {
    if (this.checkboxTarget.checked) {
      this.statusValue = "need_to_receive";
    } else if (this.allocationValue >= this.maxAllocationValue) {
      this.statusValue = "received";
    } else if (this.allocationValue > 0) {
      this.statusValue = "partially_received";
    } else {
      this.statusValue = "need_to_receive";
    }
  }

  #togglingRowCheckbox(e) {
    const { checked } = e.detail;

    this.checkboxTarget.indeterminate = false;
    this.checkboxTarget.checked = checked;
    this.checkboxChanged();
  }

  get #allocationMaxed() {
    return this.allocationValue >= this.maxAllocationValue;
  }

  #allocationUpdated(value) {
    this.inputTarget.value = value;
    this.invisibleLabelTarget.textContent = value;

    this.dirtyValue = this.allocationValue !== this.initialAllocationValue;

    this.checkboxTarget.checked = this.dirtyValue;
    this.checkboxTarget.indeterminate = this.checkboxTarget.checked && !this.#allocationMaxed;

    if (this.hasFormValueTarget) {
      this.formValueTarget.name = this.dirtyValue
        ? `line_item_size[${this.element.dataset.lineItemId}][${this.element.dataset.size}]`
        : "";
      this.formValueTarget.value = this.inputTarget.value;
    }

    if (this.emitEvents) {
      this.dispatch("update", { prefix: "allocation", target: this.element, detail: { instance: this } });
    }
  }
}
